import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchBookings,
  fetchCalendar,
  getBooking,
  createBooking,
  updateBooking,
  cancelBooking,
  checkInBooking,
  checkOutBooking,
  createWalkIn,
  moveBookingRoom,
  extendBookingStay,
  type BookingsQuery,
  type CalendarQuery,
  type CreateBookingPayload,
  type UpdateBookingPayload,
  type WalkInPayload,
  type MoveBookingPayload,
  type ExtendStayPayload,
} from '@/services/api/bookings.api';

export function useBookings(query?: BookingsQuery) {
  return useQuery({
    queryKey: ['bookings', query],
    queryFn: () => fetchBookings(query),
    enabled: !!query?.propertyId,
    staleTime: 30_000,
  });
}

/**
 * Single booking incl. `channelPayload` (E9). Fetched on-demand (e.g. when a
 * bill opens) so the lean bookings list stays lean.
 */
export function useBooking(id: string | null, enabled = true) {
  return useQuery({
    queryKey: ['bookings', 'detail', id],
    queryFn: () => getBooking(id as string),
    enabled: enabled && !!id,
    staleTime: 30_000,
  });
}

export function useCalendar(query?: CalendarQuery) {
  return useQuery({
    queryKey: ['bookings', 'calendar', query],
    queryFn: () => fetchCalendar(query),
    enabled: !!query?.propertyId,
    staleTime: 30_000,
  });
}

export function useCreateBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBookingPayload) => createBooking(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['frontdesk'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateBookingPayload }) =>
      updateBooking(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

export function useCancelBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      reason,
      reasonCodeId,
    }: {
      id: string;
      reason?: string;
      reasonCodeId?: string;
    }) =>
      cancelBooking(id, {
        ...(reason !== undefined ? { reason } : {}),
        ...(reasonCodeId !== undefined ? { reasonCodeId } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

export function useCheckInBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes, idDocumentUrl, actualCheckInTime }: { id: string; notes?: string; idDocumentUrl?: string; actualCheckInTime?: string }) =>
      checkInBooking(id, { ...(notes ? { notes } : {}), ...(idDocumentUrl ? { idDocumentUrl } : {}), ...(actualCheckInTime ? { actualCheckInTime } : {}) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['frontdesk'] });
      queryClient.invalidateQueries({ queryKey: ['units'] });
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}

export function useCreateWalkIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: WalkInPayload) => createWalkIn(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['frontdesk'] });
      queryClient.invalidateQueries({ queryKey: ['units'] });
    },
  });
}

export function useCheckOutBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes, additionalCharges, actualCheckOutTime }: { id: string; notes?: string; additionalCharges?: number; actualCheckOutTime?: string }) => {
      const payload: Parameters<typeof checkOutBooking>[1] = {};
      if (notes !== undefined) payload.notes = notes;
      if (additionalCharges !== undefined) payload.additionalCharges = additionalCharges;
      if (actualCheckOutTime !== undefined) payload.actualCheckOutTime = actualCheckOutTime;
      return checkOutBooking(id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['frontdesk'] });
      queryClient.invalidateQueries({ queryKey: ['units'] });
      queryClient.invalidateQueries({ queryKey: ['folio'] });
    },
  });
}

export function useMoveBookingRoom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: MoveBookingPayload }) =>
      moveBookingRoom(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['frontdesk'] });
      queryClient.invalidateQueries({ queryKey: ['units'] });
    },
  });
}

export function useExtendBookingStay() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ExtendStayPayload }) =>
      extendBookingStay(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['frontdesk'] });
    },
  });
}
